//GestorColumnas.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Input } from "../components/ui/input";
import { Select, SelectItem } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  fetchSavedTables,
  setColumnSettings,
  setunique_key,
  saveDashboardGroup,
  updateOrderStatus,
  fetchProductList
} from '../redux/columnsSlice';

import SimpleModal from './SimpleModal';
import { buildApiUrl } from '../utils/api';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Opciones de tipo de columna disponibles
const columnTypeOptions = [
  'Informativo',
  'Texto',
  'Número',
  'Correo',
  'Teléfono | Whatsapp', 
  'Dropdown',
  'Boolean',
  'Fecha',
  'Hora',
  'Texto Multilínea',
  'Estado del Pedido',
  'Salida de Materiales',
  'Cantidad Real de Piezas'
];

// Columnas que se administran automáticamente (no se puede cambiar tipo)
const autoManagedColumns = ['Estado del Pedido'];

// ----- Funciones auxiliares -----
function parseOptions(options) {
  if (!options) return [];
  if (typeof options === 'string') {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(options) ? options : [];
}

function normalizeColumnConfig(col) {
  return {
    column_name: col.column_name,
    alias: col.alias,
    column_type: col.column_type || 'Informativo',
    is_editable:
      col.is_editable === '1' ||
      col.is_editable === 1 ||
      col.is_editable === true,
    max_length:
      col.max_length !== undefined && col.max_length !== null
        ? col.max_length
        : 255,
    options: parseOptions(col.options),
    default_value: col.default_value || '',
    procedure_name: col.procedure_name || '',
    database_name: col.database_name || '',
    enable_call: col.enable_call ?? true,
    enable_whatsapp: col.enable_whatsapp ?? true,
    status: col.status || null, // Para lógica de color de fila en "Estado del Pedido"
  };
}

function getInitialConfigForColumnType(columnType) {
  console.log("🟢 Seleccionando tipo de columna:", columnType);
  const configs = {
    Informativo: {
      column_type: 'Informativo',
      is_editable: false,
      default_value: '',
      max_length: 255,
      options: []
    },
    Texto: {
      column_type: 'Texto',
      is_editable: true,
      default_value: '',
      max_length: 255,
      options: []
    },
    Número: {
      column_type: 'Número',
      is_editable: true,
      default_value: 0,
      min_value: 0,
      max_value: 999999999999.99,
      precision: 2,
      options: []
    },
    Correo: {
      column_type: 'Correo',
      is_editable: true,
      default_value: '',
      options: []
    },
    'Teléfono | Whatsapp': {
      column_type: 'Teléfono | Whatsapp',
      is_editable: true,
      default_value: '',
      max_length: 20,
      options: [],
      enable_call: true,
      enable_whatsapp: true
    },
    Dropdown: {
      column_type: 'Dropdown',
      is_editable: true,
      default_value: '',
      max_length: 255,
      options: [''] 
    },
    Boolean: {
      column_type: 'Boolean',
      is_editable: true,
      default_value: false,
      options: []
    },
    Fecha: {
      column_type: 'Fecha',
      is_editable: true,
      default_value: new Date().toISOString().substr(0, 10),
      options: []
    },
    Hora: {
      column_type: 'Hora',
      is_editable: true,
      default_value: '12:00',
      options: []
    },
    'Texto Multilínea': {
      column_type: 'Texto Multilínea',
      is_editable: true,
      default_value: '',
      max_length: 1000,
      options: []
    },
    'Estado del Pedido': {
      column_type: 'Estado del Pedido',
      is_editable: true,
      default_value: '',
      options: []
    },
    'Salida de Materiales': {
      column_type: 'Salida de Materiales',
      is_editable: true,
      default_value: '',
      max_length: 255,
      options: []
    },
    'Cantidad Real de Piezas': {
      column_type: 'Cantidad Real de Piezas',
      is_editable: false,
      default_value: '',
      options: []
    },
  };
  const configAsignada = configs[columnType] || configs['Informativo'];
  console.log("🔵 Configuración inicial asignada:", configAsignada);
  return configAsignada;
}

function getRowBackgroundColor(status) {
  switch (status) {
    case 'wc-pending':
      return '#FCF8E3';
    case 'wc-processing':
      return '#e3efe3';
    case 'wc-on-hold':
      return '#fbecd3';
    case 'wc-completed':
      return '#e2eaef';
    case 'wc-cancelled':
      return '#F2F2F2';
    case 'wc-refunded':
      return '#FCF8E3';
    case 'wc-failed':
      return '#F2DEDE';
    case 'wc-checkout-draft':
      return '#E2E3E5';
    default:
      return 'transparent';
  }
}
// Guarda/actualiza la configuración de una columna en la base de datos
const saveColumnSettings = async (columnData) => {
  try {
    const { unique_key, columnSettings } = columnData;
    const colName = Object.keys(columnSettings)[0];
    const currentConfig = columnSettings[colName];

    console.log("🔍 Preparando guardado de columna:", colName);
    console.log("🧩 Configuración actual a guardar:", currentConfig);
    console.log("🔑 Grupo (unique_key):", unique_key);

    const response = await axios.get(buildApiUrl('/get_dashboard_columns'), {
      params: { unique_key },
      headers: { 'X-WP-Nonce': autocrm_globals.nonce },
    });

    const existingColumns = response.data?.columns || [];

    console.log("📦 Columnas existentes en el grupo:", existingColumns.map(c => c.column_name));

    const columnExists = existingColumns.some(
      (col) => col.column_name === colName && col.unique_key === unique_key
    );

    if (columnExists) {
      console.log("✏️ Columna ya existe, actualizando...");
      await axios.put(buildApiUrl('/update_dashboard_column'), columnData, {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      console.log(`✅ Columna "${colName}" actualizada correctamente.`);
    } else {
      console.log("🆕 Columna nueva, guardando por primera vez...");
      await axios.post(buildApiUrl('/save_column_configurations'), columnData, {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      console.log(`✅ Nueva columna "${colName}" guardada.`);
    }

    console.log("📦 Payload final enviado al backend:", columnData);

  } catch (error) {
    console.error("❌ Error al guardar la columna:", error.response?.data || error.message);
    throw error;
  }
};


const GestorColumnas = () => {
  const dispatch = useDispatch();

  const {
    savedTables,
    savedTablesLoading,
    savedTablesError,
    columnSettings,
    loading,
    error,
    unique_key,
    productList,
  } = useSelector((state) => state.columns);

  console.log("🧩 productList desde Redux:", productList); // ✅ AHORA sí está OK


  const [selectedGroup, setSelectedGroup] = useState('');
  const [fetchedData, setFetchedData] = useState(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [dirtyRows, setDirtyRows] = useState({});
  const [aliasInputs, setAliasInputs] = useState({});
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    destinatario: '',
    asunto: '',
    mensaje: '',
  });
  const [orderedColumns, setOrderedColumns] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // 🔄 Carga de productos desde Redux
  useEffect(() => {
    console.log("🧩 Disparando fetchProductList desde GestorColumnas");
    dispatch(fetchProductList());
  }, [dispatch]);

  // Cargar grupos guardados
  useEffect(() => {
    dispatch(fetchSavedTables())
      .unwrap()
      .catch((err) => console.error('Error al obtener grupos guardados:', err));
  }, [dispatch]);

  //  Al cambiar selectedGroup, recargamos configuraciones y columnas
  const handleGroupChange = (newGroup) => {
    setSelectedGroup(newGroup);
    setConfigLoaded(false);
  };

  useEffect(() => {
    if (!selectedGroup || configLoaded) return;

    console.log('Grupo seleccionado (unique_key):', selectedGroup);
    dispatch(setunique_key(selectedGroup));

    const requestParams = {
      unique_key: selectedGroup,
      fechaInicio: '2024-12-31',
      fechaFin: '2100-01-01',
      perPage: 10,
      page: 1,
      p_column_filter: '',
      p_field_filter: '',
    };

    console.log("📌 Parámetros enviados a la API:", requestParams);

    axios
      .get(buildApiUrl('/get_dashboard_columns'), {
        params: requestParams,
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      })
      .then((resp) => {
        console.log("✅ Respuesta completa de la API:", resp.data);
        if (resp.data && resp.data.columns) {
          setFetchedData(resp.data);
        } else {
          console.error("❌ No se obtuvieron columnas", resp.data);
          setFetchedData(null);
        }
      })
      .catch((err) => {
        console.error("❌ Error en la API:", err.response?.data || err.message);
        setFetchedData(null);
      });

    setDirtyRows({});
  }, [selectedGroup, configLoaded, dispatch]);

  //  Extraer datos del grupo seleccionado
  const selectedGroupData = useMemo(() => {
    return savedTables.find((g) => g.unique_key === selectedGroup) || {};
  }, [savedTables, selectedGroup]);

  //  Obtener configuración de columnas guardada en Redux
  const normalizedColumnSettingsObject = useMemo(() => {
    if (!unique_key) return {};
    return columnSettings[unique_key] || {};
  }, [unique_key, columnSettings]);

  //  Combinar columnas del API con la configuración en Redux
  const finalColumns = useMemo(() => {
    const unifyCols = fetchedData?.columns || [];
    return unifyCols.map((col) => {
      const base = normalizeColumnConfig(col);
      const savedCfg = normalizedColumnSettingsObject[col.column_name] || {};
      return {
        ...base,
        ...savedCfg,
        order:
          savedCfg.order !== undefined
            ? savedCfg.order
            : col.order !== undefined
            ? col.order
            : 9999,
      };
    });
  }, [fetchedData, normalizedColumnSettingsObject]);

  //  Ordenar columnas por "order"
  useEffect(() => {
    if (fetchedData && fetchedData.columns) {
      const sortedColumns = fetchedData.columns
        .slice()
        .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
      setOrderedColumns(sortedColumns);
    }
  }, [fetchedData]);

  //  Actualizar configuración local
  const updateColumnConfig = useCallback(
    (columnName, updatedFields) => {
      const current = normalizedColumnSettingsObject[columnName] || {};
      const newCfg = { ...current, ...updatedFields };
      dispatch(setColumnSettings({ columnName, settings: newCfg }));
      setDirtyRows((prev) => ({ ...prev, [columnName]: true }));
    },
    [dispatch, normalizedColumnSettingsObject]
  );

  //  Manejo de alias
  const handleLocalAliasChange = useCallback((columnName, value) => {
    setAliasInputs((prev) => ({ ...prev, [columnName]: value }));
    setDirtyRows((prev) => ({ ...prev, [columnName]: true }));
  }, []);

  //  Cambio de tipo de columna
  const handleColumnTypeChange = useCallback(
    (columnName, newType) => {
      if (autoManagedColumns.includes(columnName)) {
        setAlertMessage('No es posible cambiar el tipo de esta columna (auto-gestionada).');
        setIsAlertModalOpen(true);
        return;
      }
      console.log(`🟠 Cambiando tipo de columna "${columnName}" a:`, newType);
      const base = getInitialConfigForColumnType(newType);
      updateColumnConfig(columnName, { ...base, column_type: newType });
      setFetchedData((prev) => {
        if (!prev || !prev.columns) return prev;
        return {
          ...prev,
          columns: prev.columns.map((col) =>
            col.column_name === columnName
              ? { ...col, column_type: newType }
              : col
          ),
        };
      });
      console.log("🟣 Nueva configuración aplicada:", base);
    },
    [updateColumnConfig]
  );

  //  Guardar la configuración de una columna
  const handleSaveColumnConfig = useCallback(
    async (colName) => {
      console.log("🟢 [handleSaveColumnConfig] Iniciando guardado para columna:", colName);
  
      if (autoManagedColumns.includes(colName)) {
        console.warn("⚠️ [handleSaveColumnConfig] Columna auto-gestionada, no se puede modificar:", colName);
        setAlertMessage('No es posible configurar esta columna (auto-gestionada).');
        setIsAlertModalOpen(true);
        return;
      }
  
      const currentType =
        normalizedColumnSettingsObject[colName]?.column_type ||
        finalColumns.find((c) => c.column_name === colName)?.column_type ||
        'Informativo';
  
      const cfg =
        normalizedColumnSettingsObject[colName] ||
        getInitialConfigForColumnType(currentType);
  
      const payloadSettings = {
        ...cfg,
        column_type: currentType || 'Informativo',
        is_editable:
          typeof cfg.is_editable !== 'undefined'
            ? cfg.is_editable
            : currentType !== 'Informativo',
        max_length: cfg.max_length ?? 255,
        options: Array.isArray(cfg.options) ? cfg.options : [],
        alias: (aliasInputs[colName] ?? '').trim() || cfg.alias || '',
        enable_call: cfg.enable_call ?? true,
        enable_whatsapp: cfg.enable_whatsapp ?? true,
      };
  
      const finalPayload = {
        unique_key: unique_key || selectedGroup,
        columnSettings: { [colName]: payloadSettings },
      };
  
      console.log("📦 [handleSaveColumnConfig] Payload listo para enviar:", finalPayload);
  
      setIsSaving(true);
      try {
        await saveColumnSettings(finalPayload);
        console.log("✅ [handleSaveColumnConfig] Guardado exitoso para columna:", colName);
        setDirtyRows((prev) => ({ ...prev, [colName]: false }));
      } catch (err) {
        console.error("❌ [handleSaveColumnConfig] Error al guardar columna:", err);
        setAlertMessage(
          typeof err === 'object'
            ? err.message || JSON.stringify(err)
            : String(err)
        );
        setIsAlertModalOpen(true);
      } finally {
        setIsSaving(false);
      }
    },
    [normalizedColumnSettingsObject, finalColumns, aliasInputs, unique_key, selectedGroup]
  );  

  //  Manejo de email (simple)
  const handleSendEmail = useCallback(() => {
    axios
      .post(buildApiUrl('/send_email'), {
        destinatario: emailForm.destinatario,
        asunto: emailForm.asunto,
        mensaje: emailForm.mensaje,
      })
      .then(() => {
        setAlertMessage("Correo enviado exitosamente.");
        setIsAlertModalOpen(true);
        setIsEmailModalOpen(false);
        setEmailForm({ destinatario: '', asunto: '', mensaje: '' });
      })
      .catch((err) => {
        setAlertMessage(
          typeof err === 'object'
            ? err.message || JSON.stringify(err)
            : String(err)
        );
        setIsAlertModalOpen(true);
      });
  }, [emailForm]);

  //  Drag & Drop para reordenar columnas
  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const newOrder = [...orderedColumns];
      const [movedItem] = newOrder.splice(result.source.index, 1);
      newOrder.splice(result.destination.index, 0, movedItem);
      const updatedColumns = newOrder.map((col, idx) => ({
        ...col,
        order: idx,
      }));
      setOrderedColumns(updatedColumns);
      dispatch(
        setColumnSettings({
          unique_key,
          settings: updatedColumns.reduce((acc, col) => {
            acc[col.column_name] = { ...col, order: col.order };
            return acc;
          }, {}),
        })
      );
      console.log("✅ Nuevo orden de columnas:", updatedColumns);
    },
    [dispatch, orderedColumns, unique_key]
  );

  //  Manejo de opciones en 'Dropdown'
  const handleDropdownOptionChange = useCallback(
    (columnName, idx, newValue) => {
      const cfg = normalizedColumnSettingsObject[columnName] || {};
      const currentOptions = Array.isArray(cfg.options) ? [...cfg.options] : [];
      currentOptions[idx] = newValue;
      updateColumnConfig(columnName, { options: currentOptions });
      console.log("🔶 Opciones después del cambio:", currentOptions);
    },
    [normalizedColumnSettingsObject, updateColumnConfig]
  );

  const handleAddDropdownOption = useCallback(
    (columnName) => {
      const col = finalColumns.find((c) => c.column_name === columnName);
      const currentOptions = Array.isArray(col?.options) ? [...col.options] : [];
      currentOptions.push('');
      updateColumnConfig(columnName, { options: currentOptions });
    },
    [finalColumns, updateColumnConfig]
  );

  const handleRemoveDropdownOption = useCallback(
    (columnName, idx) => {
      const col = finalColumns.find((c) => c.column_name === columnName);
      const currentOptions = col && Array.isArray(col.options) ? [...col.options] : [];
      currentOptions.splice(idx, 1);
      updateColumnConfig(columnName, { options: currentOptions });
    },
    [finalColumns, updateColumnConfig]
  );

  //  Renderizado avanzado de cada columna según su tipo
  const renderColumnConfigInterface = (col) => {
    if (
      !col.column_type ||
      col.column_type === 'Informativo' ||
      autoManagedColumns.includes(col.column_name)
    ) {
      return null;
    }

    console.log("🟡 Renderizando configuración para columna:", col.column_name);

    const updatedCfg = normalizedColumnSettingsObject[col.column_name] || {};
    let currentOptions =
      Array.isArray(updatedCfg.options) && updatedCfg.options.length > 0
        ? updatedCfg.options
        : Array.isArray(col.options) && col.options.length > 0
          ? col.options
          : [];

    if (col.column_type === 'Dropdown' && currentOptions.length === 0) {
      currentOptions = [''];
    }

    switch (col.column_type) {
      case 'Dropdown':
        return (
          <div className="autocrm-dropdown-config">
            <label className="autocrm-label">Opciones:</label>
            {currentOptions.map((opt, idx) => (
              <div key={idx} className="autocrm-option-row">
                <Input
                  value={opt}
                  onChange={(e) =>
                    handleDropdownOptionChange(col.column_name, idx, e.target.value)
                  }
                  className="autocrm-input"
                />
                <Button
                  variant="destructive"
                  onClick={() => handleRemoveDropdownOption(col.column_name, idx)}
                  className="autocrm-btn autocrm-btn-danger"
                >
                  X
                </Button>
              </div>
            ))}
            <Button
              variant="secondary"
              onClick={() => handleAddDropdownOption(col.column_name)}
              className="autocrm-btn"
            >
              + Opción
            </Button>
          </div>
        );

      case 'Número':
        return (
          <div className="autocrm-number-config">
            <label className="autocrm-label">Mínimo:</label>
            <Input
              type="number"
              value={col.min_value ?? 0}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  min_value: parseFloat(e.target.value) || 0,
                })
              }
              className="autocrm-input"
            />
            <label className="autocrm-label">Máximo:</label>
            <Input
              type="number"
              value={col.max_value ?? 999999999999.99}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  max_value: parseFloat(e.target.value) || 999999999999.99,
                })
              }
              className="autocrm-input"
            />
            <label className="autocrm-label">Precisión:</label>
            <Input
              type="number"
              value={col.precision ?? 2}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  precision: parseInt(e.target.value, 10) || 2,
                })
              }
              className="autocrm-input"
            />
          </div>
        );

      case 'Teléfono | Whatsapp':
        return (
          <div className="autocrm-phone-config">
            <label className="autocrm-label">Longitud Máxima:</label>
            <Input
              type="number"
              value={col.max_length || 20}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  max_length: parseInt(e.target.value, 10) || 20,
                })
              }
              className="autocrm-input"
            />
            <label className="autocrm-label">Habilitar Llamadas:</label>
            <input
              type="checkbox"
              checked={col.enable_call || false}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  enable_call: e.target.checked,
                })
              }
              className="autocrm-checkbox"
            />
            <label className="autocrm-label">Habilitar WhatsApp:</label>
            <input
              type="checkbox"
              checked={col.enable_whatsapp || false}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  enable_whatsapp: e.target.checked,
                })
              }
              className="autocrm-checkbox"
            />
          </div>
        );

      case 'Texto':
      case 'Texto Multilínea':
        return (
          <div className="autocrm-text-config">
            <label className="autocrm-label">Longitud Máxima:</label>
            <Input
              type="number"
              value={col.max_length || (col.column_type === 'Texto' ? 255 : 1000)}
              onChange={(e) =>
                updateColumnConfig(col.column_name, {
                  max_length: parseInt(e.target.value, 10) || (col.column_type === 'Texto' ? 255 : 1000),
                })
              }
              className="autocrm-input"
            />
          </div>
        )

        case 'Cantidad Real de Piezas':
          console.log("🧩 Opciones actuales en columna 'Cantidad Real de Piezas':", currentOptions);
          return (
            <div className="autocrm-piece-config">
              <label className="autocrm-label">Productos y piezas por paquete:</label>
              {currentOptions.map((item, idx) => {
                console.log(`🔹 Renderizando fila de producto en índice ${idx}:`, item);
                return (
                  <div key={idx} className="autocrm-product-row">
                    <Select
                      value={item.product_id || ''}
                      onValueChange={(val) =>
                        updateColumnConfig(col.column_name, {
                          options: currentOptions.map((opt, i) =>
                            i === idx ? { ...opt, product_id: val } : opt
                          ),
                        })
                      }
                    >
                     <SelectItem value="">Seleccione...</SelectItem>
{productList
  .filter(
    (prod) =>
      String(prod.id).trim() !== '' &&
      prod.name?.toLowerCase().trim() !== 'seleccione...'
  )
  .map((prod) => (
    <SelectItem key={prod.id} value={String(prod.id)}>
      {prod.name}
    </SelectItem>
  ))}
                    </Select>
        
                    <Input
                      type="number"
                      placeholder="Piezas por paquete"
                      value={item.piezas_por_paquete || ''}
                      onChange={(e) =>
                        updateColumnConfig(col.column_name, {
                          options: currentOptions.map((opt, i) =>
                            i === idx
                              ? { ...opt, piezas_por_paquete: parseInt(e.target.value, 10) || 0 }
                              : opt
                          ),
                        })
                      }
                    />
        
                    <Button
                      variant="destructive"
                      onClick={() =>
                        updateColumnConfig(col.column_name, {
                          options: currentOptions.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      X
                    </Button>
                  </div>
                );
              })}
              <Button
                onClick={() =>
                  updateColumnConfig(col.column_name, {
                    options: [...currentOptions, { product_id: '', piezas_por_paquete: 0 }],
                  })
                }
              >
                + Producto
              </Button>
            </div>
          );
      default:
        return null;
    }
  };

  //  Render de cada fila de la tabla
  const renderTableRow = (col, index) => (
    <Draggable key={col.column_name} draggableId={col.column_name} index={index}>
      {(provided) => {
        const backgroundColor = col.status
          ? getRowBackgroundColor(col.status)
          : 'transparent';

        return (
          <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...provided.draggableProps.style,
              backgroundColor,
            }}
          >
            <td>{col.column_name}</td>
            <td>
              <div className="autocrm-alias-field">
                <Input
                  value={aliasInputs[col.column_name] ?? (col.alias || '')}
                  onChange={(e) => handleLocalAliasChange(col.column_name, e.target.value)}
                  className="autocrm-input"
                />
              </div>
            </td>
            <td>
              <Select
                value={col.column_type}
                onValueChange={(val) => handleColumnTypeChange(col.column_name, val)}
              >
                {columnTypeOptions.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </Select>
            </td>
            <td>
              {renderColumnConfigInterface(col)}
              <Button
  className="autocrm-save-config-btn"
  onClick={() => {
    console.log("🧪 Click en botón Guardar - Columna:", col.column_name);
    console.log("📝 Configuración antes de guardar:", normalizedColumnSettingsObject[col.column_name]);
    handleSaveColumnConfig(col.column_name);
  }}
  disabled={!dirtyRows[col.column_name] || isSaving}
>
  Guardar
</Button>
            </td>
          </tr>
        );
      }}
    </Draggable>
  );

  //  Guardar configuración general del Dashboard
  const handleSendToDashboard = useCallback(() => {
    console.log("🟢 [handleSendToDashboard] Iniciando guardado de Dashboard");
  
    const realKey = unique_key || selectedGroup;
    if (!realKey) {
      console.warn("⚠️ [handleSendToDashboard] No se ha seleccionado un grupo");
      setAlertMessage("Debes seleccionar un grupo antes de guardar.");
      setIsAlertModalOpen(true);
      return;
    }
  
    const payload = {
      unique_key: realKey,
      group_name: selectedGroupData.group_name || 'Sin nombre',
      database_name: selectedGroupData.database_name || '',
      procedures: selectedGroupData.procedures || [],
      parameters: selectedGroupData.parameters || {
        fechaInicio: '2024-12-31',
        fechaFin: '2100-01-01',
        perPage: 10,
        page: 1,
        p_column_filter: '',
        p_field_filter: ''
      },
      columns: orderedColumns,
      data: fetchedData?.rows || [],
    };
  
    console.log("📦 [handleSendToDashboard] Payload a enviar:", payload);
  
    dispatch(saveDashboardGroup(payload))
      .unwrap()
      .then(() => {
        console.log("✅ [handleSendToDashboard] Guardado exitoso, refrescando columnas...");
        return axios.get(buildApiUrl('/get_dashboard_columns'), {
          params: {
            unique_key: realKey,
            fechaInicio: '2024-12-31',
            fechaFin: '2100-01-01',
            perPage: 10,
            page: 1,
            p_column_filter: '',
            p_field_filter: ''
          },
          headers: { 'X-WP-Nonce': autocrm_globals.nonce },
        });
      })
      .then((resp) => {
        if (resp.data && resp.data.columns) {
          console.log("🔄 [handleSendToDashboard] Refrescadas columnas desde backend:", resp.data.columns);
          setFetchedData(resp.data);
          setAlertMessage("¡Dashboard guardado y refrescado con éxito!");
          setIsAlertModalOpen(true);
        } else {
          console.warn("⚠️ [handleSendToDashboard] Respuesta sin columnas:", resp.data);
        }
      })
      .catch((err) => {
        console.error("❌ [handleSendToDashboard] Error al guardar el Dashboard:", err);
        setAlertMessage(
          typeof err === 'object'
            ? err.message || JSON.stringify(err)
            : String(err)
        );
        setIsAlertModalOpen(true);
      });
  }, [unique_key, selectedGroup, selectedGroupData, orderedColumns, fetchedData, dispatch]);
  
  //  Actualizar estado del pedido (no cambia la config)
  const handleUpdateOrderStatus = useCallback(
    (order_id, new_status) => {
      dispatch(updateOrderStatus({ unique_key, order_id, new_status }))
        .unwrap()
        .then((res) => {
          setFetchedData((prev) => {
            if (!prev || !prev.rows) return prev;
            return {
              ...prev,
              rows: prev.rows.map((row) =>
                row.order_id === order_id
                  ? { ...row, ...res.updatedRow }
                  : row
              ),
            };
          });
        })
        .catch((err) => {
          console.error('Error al actualizar estado:', err);
        });
    },
    [dispatch, unique_key]
  );

  // Render principal
  return (
    <div className="autocrm-dashboard-container">
      <Card className="autocrm-card">
        <CardContent className="autocrm-card-content">
          <h1 className="autocrm-title">Gestor de Columnas</h1>

          {/* Selección de grupo */}
          <div className="autocrm-group-select">
            <label htmlFor="groupSelect" className="autocrm-label">
              Selecciona un Grupo Guardado:
            </label>
            {savedTablesLoading ? (
              <p className="autocrm-loading">Cargando grupos...</p>
            ) : savedTablesError ? (
              <p className="autocrm-error-message">
                {typeof savedTablesError === 'object'
                  ? JSON.stringify(savedTablesError)
                  : savedTablesError}
              </p>
            ) : (
              <Select value={selectedGroup} onValueChange={handleGroupChange}>
                {savedTables.map((grp) => (
                  <SelectItem key={grp.unique_key} value={grp.unique_key}>
                    {grp.group_name || 'Sin nombre'}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>

          {/* Datos del grupo */}
          {selectedGroup && (
            <div className="autocrm-selected-group">
              <h2 className="autocrm-group-title">
                Grupo Seleccionado: {selectedGroupData.group_name}
              </h2>
            </div>
          )}

          {/* Si no hay grupo seleccionado, mensaje placeholder */}
          {!selectedGroup ? (
            <p className="autocrm-placeholder">Selecciona un grupo para ver las columnas.</p>
          ) : (
            <>
              {loading && <p className="autocrm-loading">Cargando columnas...</p>}
              {error && (
                <p className="autocrm-error-message">
                  {typeof error === 'object' ? (error.message || JSON.stringify(error)) : error}
                </p>
              )}

              {/* Tabla de columnas */}
              {!loading && (
                <div className="autocrm-columns-list">
                  <h3 className="autocrm-subtitle">Lista de Columnas</h3>
                  <div className="autocrm-data-table-container">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="columns">
                        {(provided) => (
                          <table
                            className="autocrm-data-table"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            <thead className="autocrm-data-table-head">
                              <tr>
                                <th>Nombre Original</th>
                                <th>Alias</th>
                                <th>Tipo de Columna</th>
                                <th>Configuración Avanzada</th>
                              </tr>
                            </thead>
                            <tbody className="autocrm-data-table-body">
                              {orderedColumns && orderedColumns.length > 0 ? (
                                orderedColumns.map((col, index) => renderTableRow(col, index))
                              ) : (
                                <tr>
                                  <td colSpan={4}>No hay columnas para este grupo</td>
                                </tr>
                              )}
                              {provided.placeholder}
                            </tbody>
                          </table>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Botón para salvar configuración general del Dashboard */}
          <div className="autocrm-save-dashboard">
            <Button onClick={handleSendToDashboard} className="autocrm-btn">
              Salvar Dashboard
            </Button>
          </div>

          {/* Modal de alerta */}
          <SimpleModal
            isOpen={isAlertModalOpen}
            onClose={() => setIsAlertModalOpen(false)}
          >
            <h2>Información</h2>
            <p>
              {typeof alertMessage === 'object'
                ? alertMessage.message || JSON.stringify(alertMessage)
                : alertMessage}
            </p>
            <Button onClick={() => setIsAlertModalOpen(false)} className="autocrm-btn">
              Cerrar
            </Button>
          </SimpleModal>

          {/* Modal para envío de correo */}
          {isEmailModalOpen && (
            <SimpleModal
              isOpen={isEmailModalOpen}
              onClose={() => setIsEmailModalOpen(false)}
            >
              <h2>Enviar Correo</h2>
              <div className="autocrm-email-field">
                <label className="autocrm-label">Destinatario:</label>
                <Input
                  value={emailForm.destinatario}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, destinatario: e.target.value })
                  }
                  className="autocrm-input"
                />
              </div>
              <div className="autocrm-email-field">
                <label className="autocrm-label">Asunto:</label>
                <Input
                  value={emailForm.asunto}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, asunto: e.target.value })
                  }
                  className="autocrm-input"
                />
              </div>
              <div className="autocrm-email-field">
                <label className="autocrm-label">Mensaje:</label>
                <textarea
                  value={emailForm.mensaje}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, mensaje: e.target.value })
                  }
                  className="autocrm-textarea"
                />
              </div>
              <Button onClick={handleSendEmail} className="autocrm-btn">
                Enviar
              </Button>
            </SimpleModal>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GestorColumnas;
